import { Button, Dialog, Input } from "@bafe/ui";
import type { ReactNode } from "react";
import { useId } from "react";

import { ConfirmPhraseCopy, confirmPhrasesMatch } from "./ConfirmPhraseCopy";
import { ErrorPanel } from "./ErrorPanel";

export interface ConfirmDialogProps {
	open: boolean;
	onClose: () => void;
	title: string;
	description?: ReactNode;
	children?: ReactNode;
	confirmLabel: string;
	onConfirm: () => void;
	pending?: boolean;
	danger?: boolean;
	/** Exact value the customer must type before the action is enabled. */
	confirmPhrase?: string;
	confirmPhraseLabel?: string;
	confirmValue?: string;
	onConfirmValueChange?: (value: string) => void;
	error?: unknown;
	size?: "sm" | "md" | "lg";
	/** When true, the confirm button stays disabled even if the phrase matches. */
	confirmDisabled?: boolean;
}

export function ConfirmDialog({
	open,
	onClose,
	title,
	description,
	children,
	confirmLabel,
	onConfirm,
	pending = false,
	danger = false,
	confirmPhrase,
	confirmPhraseLabel,
	confirmValue = "",
	onConfirmValueChange,
	error,
	size = "md",
	confirmDisabled = false,
}: ConfirmDialogProps) {
	const confirmInputId = useId();
	const phraseMatches =
		confirmPhrase === undefined || confirmPhrasesMatch(confirmValue, confirmPhrase);
	const canConfirm = phraseMatches && !confirmDisabled;

	if (!open) return null;

	return (
		<Dialog
			open
			onClose={onClose}
			title={title}
			description={description}
			dismissable={!pending}
			size={size}
			footer={
				<>
					<Button variant="ghost" onClick={onClose} disabled={pending}>
						Cancel
					</Button>
					<Button
						variant={danger ? "danger" : "primary"}
						onClick={onConfirm}
						pending={pending}
						pendingLabel="Working…"
						disabled={!canConfirm}
					>
						{confirmLabel}
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-4">
				{children}

				{confirmPhrase !== undefined && (
					<div className="flex flex-col gap-1.5">
						<div className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5 text-sm font-medium text-(--text-primary)">
							{confirmPhraseLabel ? (
								<label htmlFor={confirmInputId}>{confirmPhraseLabel}</label>
							) : (
								<label htmlFor={confirmInputId} className="inline-flex flex-wrap items-baseline gap-1">
									<span>Type</span>
									<ConfirmPhraseCopy phrase={confirmPhrase} inputId={confirmInputId} />
									<span>to confirm</span>
								</label>
							)}
						</div>
						<p className="text-xs text-(--text-muted)">
							This step exists so a mis-click cannot do it for you.
						</p>
						<Input
							id={confirmInputId}
							value={confirmValue}
							autoComplete="off"
							autoCapitalize="none"
							spellCheck={false}
							className="text-dns"
							onChange={(event) => onConfirmValueChange?.(event.target.value)}
						/>
					</div>
				)}

				{error !== undefined && error !== null && <ErrorPanel error={error} />}
			</div>
		</Dialog>
	);
}
